import { App } from "@capacitor/app";
import { setBackupRestoreContext } from "../data/uploadQueue";
import { preserveKnownPreferenceLists } from "./profilePreferenceContinuity";
import { flushPreferenceOutbox } from "../preferences/preferenceOutbox";
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { onAuthStateChanged, type User } from "firebase/auth";
import { auth } from "../firebase/client";
import { loadCachedProfile, loadProfile, syncProfileEmailFromAuth, watchProfile } from "./profileService";
import { logout } from "./authService";
import { registerCurrentDevice, watchCurrentDeviceRevocation } from "../security/deviceSessions";
import { enablePushForCurrentDevice } from "../notifications/pushService";
import type { UserProfile } from "../types/domain";

import { canRestoreProfile, loadStartupProfile } from "./startupProfile";

interface AuthState {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const generationRef = useRef(0);
  const refreshSequenceRef = useRef(0);

  async function readActiveProfile(uid: string): Promise<UserProfile | null> {
    let next = await loadProfile(uid);
    if (next && auth.currentUser?.uid === uid && auth.currentUser.email && next.email?.toLowerCase() !== auth.currentUser.email.toLowerCase()) {
      await syncProfileEmailFromAuth().catch(() => undefined);
      next = await loadProfile(uid);
    }
    return next;
  }

  async function refreshProfile() {
    if (!auth.currentUser) {
      setProfile(null);
      return;
    }
    const uid = auth.currentUser.uid;
    const generation = generationRef.current;
    const sequence = ++refreshSequenceRef.current;
    const next = await readActiveProfile(uid);
    if (generationRef.current === generation && refreshSequenceRef.current === sequence && auth.currentUser?.uid === uid) setProfile(previous => preserveKnownPreferenceLists(previous, next));
  }

  useEffect(() => {
    let stopDeviceWatch: (() => void) | undefined;
    let stopProfileWatch: (() => void) | undefined;
    let cancelled = false;
    const unsubscribe = onAuthStateChanged(auth, async (next) => {
      const generation = ++generationRef.current;
      const startupSequence = ++refreshSequenceRef.current;
      let restoredCached = false;
      let profilePublished = false;
      let serverProfileSeen = false;
      const isCurrent = () => !cancelled && generationRef.current === generation && auth.currentUser?.uid === next?.uid;
      stopDeviceWatch?.();
      stopDeviceWatch = undefined;
      stopProfileWatch?.();
      stopProfileWatch = undefined;
      if (isCurrent()) {
        setLoading(true);
        setProfile(null);
      }
      setUser(next);
      if (next) {
        stopProfileWatch=watchProfile(next.uid,(value,metadata)=>{
          if(!isCurrent()||(metadata.fromCache&&serverProfileSeen&&!metadata.hasPendingWrites))return;
          if(!metadata.fromCache)serverProfileSeen=true;
          refreshSequenceRef.current+=1;
          profilePublished=true;
          setProfile(previous => preserveKnownPreferenceLists(previous, value));setLoading(false);
        });
        try {
          const nextProfile = await loadStartupProfile(
            () => loadCachedProfile(next.uid),
            () => readActiveProfile(next.uid),
            (cached) => {
              if (isCurrent() && refreshSequenceRef.current === startupSequence && canRestoreProfile(cached, next.uid, next.email)) {
                restoredCached = true;
                setProfile(previous => preserveKnownPreferenceLists(previous, cached));
                setLoading(false);
              }
            }
          );
          if (!isCurrent()) return;
          if (isCurrent() && refreshSequenceRef.current === startupSequence) {
            serverProfileSeen=true;
            profilePublished=true;
            setProfile(previous => preserveKnownPreferenceLists(previous, nextProfile));
            setLoading(false);
          }

          if (nextProfile) {
            if (nextProfile.preferences?.notifications?.collaboration !== false) {
              void enablePushForCurrentDevice(next.uid).catch(() => false);
            }
            void registerCurrentDevice(next.uid)
              .then(async () => {
                if (!isCurrent()) return;
                const stop = await watchCurrentDeviceRevocation(
                  next.uid,
                  () => void logout()
                ).catch(() => undefined);

                if (isCurrent()) stopDeviceWatch = stop;
                else stop?.();
              })
              .catch(() => undefined);
          }
        } catch {
          if (isCurrent() && !restoredCached && !profilePublished && refreshSequenceRef.current === startupSequence) setProfile(null);
        }
      } else if (isCurrent()) {
        setProfile(null);
      }
      if (isCurrent()) setLoading(false);
    });
    return () => {
      cancelled = true;
      generationRef.current += 1;
      unsubscribe();
      stopDeviceWatch?.();
      stopProfileWatch?.();
    };
  }, []);

  useEffect(()=>{
    if(!user?.uid)return;
    const uid=user.uid;
    const sync=()=>{if(auth.currentUser?.uid===uid)void flushPreferenceOutbox(uid).catch(()=>undefined)};
    const changed=(event:Event)=>{if((event as CustomEvent).detail?.uid===uid)void refreshProfile().catch(()=>undefined)};
    window.addEventListener("online",sync);window.addEventListener("focus",sync);window.addEventListener("mundely:preferences-updated",changed);
    const timer=window.setInterval(sync,15000);sync();
    return()=>{window.removeEventListener("online",sync);window.removeEventListener("focus",sync);window.removeEventListener("mundely:preferences-updated",changed);window.clearInterval(timer)};
  },[user?.uid]);

  useEffect(()=>{
    if(user?.uid&&profile?.uid===user.uid)setBackupRestoreContext(user.uid,(profile as UserProfile & {lastBackupRestoreOperation?:string}).lastBackupRestoreOperation);
  },[user?.uid,profile]);

  // Presence is independent of notification permission and records only technical device metadata.
  useEffect(() => {
    if (!user?.uid || profile?.uid !== user.uid) return;
    const uid = user.uid;
    let stopped = false, inFlight = false, lastSuccess = 0;
    const sync = async () => {
      if (stopped || inFlight || auth.currentUser?.uid !== uid || document.visibilityState === "hidden" || Date.now() - lastSuccess < 60000) return;
      inFlight = true;
      try { await registerCurrentDevice(uid); if (!stopped) lastSuccess = Date.now(); } catch { /* Retry on next resume/online event; login remains available. */ }
      finally { inFlight = false; }
    };
    const resume = () => { void sync(); };
    window.addEventListener("focus", resume); window.addEventListener("online", resume);
    document.addEventListener("visibilitychange", resume);
    const timer = window.setInterval(resume, 5 * 60 * 1000);
    const native = App.addListener("appStateChange", state => { if (state.isActive) resume(); }).catch(() => null);
    resume();
    return () => { stopped = true; window.clearInterval(timer);window.removeEventListener("focus", resume);window.removeEventListener("online", resume);document.removeEventListener("visibilitychange", resume);void native.then(handle => handle?.remove()); };
  }, [user?.uid, profile?.uid]);

  const value = useMemo(() => ({ user, profile, loading, refreshProfile }), [user, profile, loading]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AuthProvider missing");
  return value;
}
